/**
 * Install Helper Utilities
 * 
 * Environment detection and PWA install prompt management for the app.
 * Handles detection of OS, browser, display mode, and provides install strategies.
 */
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from './safeStorage';

// ============================================================================
// Environment Detection
// ============================================================================

/**
 * Detect the current environment (OS, browser, display mode)
 * Returns an object with flags and an environment label
 */
export const detectEnvironment = () => {
  const ua = navigator.userAgent.toLowerCase();
  
  // OS Detection
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isDesktop = !isIOS && !isAndroid;
  
  // Check if running in standalone mode (already installed)
  const isStandalone = 
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true; // iOS Safari
  
  // Browser Detection
  // Check for Chromium-based browsers (Chrome, Edge, Opera, Samsung Internet, etc.)
  const isChromium = 
    !!window.chrome || 
    /crios|edg|opr|samsung/.test(ua);
  
  const isFirefox = /firefox/.test(ua);
  
  // Safari detection: not Chromium, not Firefox, contains safari
  const isSafari = !isChromium && !isFirefox && /safari/.test(ua);
  
  // Determine environment label
  let label = 'unknown';
  
  if (isIOS) {
    label = 'iosSafariLike'; // All iOS browsers use WebKit
  } else if (isAndroid) {
    label = isChromium ? 'androidChromium' : 'androidOther';
  } else if (isDesktop) {
    if (isChromium) {
      label = 'desktopChromium';
    } else if (isSafari) {
      label = 'desktopSafari';
    } else {
      label = 'desktopOther';
    }
  }
  
  // Check if Mac for keyboard shortcut instructions
  const isMac = /macintosh|mac os x/.test(ua);
  
  return {
    isIOS,
    isAndroid,
    isDesktop,
    isStandalone,
    isChromium,
    isFirefox,
    isSafari,
    isMac,
    label
  };
};

// ============================================================================
// beforeinstallprompt Management
// ============================================================================

let deferredInstallEvent = null;
let installAvailableCallbacks = [];

/**
 * Set up the beforeinstallprompt listener
 * Should be called once at app startup or when install helper mounts
 * @param {Function} onAvailable - Optional callback when install prompt becomes available
 */
export const setupInstallPromptListener = (onAvailable) => {
  // Add callback to list if provided
  if (typeof onAvailable === 'function') {
    installAvailableCallbacks.push(onAvailable);
  }
  
  // If we already have a deferred event, call the callback immediately
  if (deferredInstallEvent && typeof onAvailable === 'function') {
    onAvailable();
    return;
  }
  
  // Only set up listener once
  if (window.__installPromptListenerSetup) {
    return;
  }
  
  window.__installPromptListenerSetup = true;
  
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    
    // Store the event for later use
    deferredInstallEvent = e;
    
    // Notify all registered callbacks
    installAvailableCallbacks.forEach(cb => {
      try {
        cb();
      } catch (err) {
        console.warn('[InstallHelper] Callback error:', err);
      }
    });
  });
  
  // Listen for successful app install
  window.addEventListener('appinstalled', () => {
    console.log('[InstallHelper] App was installed');
    deferredInstallEvent = null;
  });
};

/**
 * Remove a callback from the install available list
 * @param {Function} callback - The callback to remove
 */
export const removeInstallPromptCallback = (callback) => {
  installAvailableCallbacks = installAvailableCallbacks.filter(cb => cb !== callback);
};

/**
 * Check if the native install prompt is available
 * @returns {boolean}
 */
export const isInstallPromptAvailable = () => {
  return deferredInstallEvent !== null;
};

/**
 * Trigger the native install prompt
 * @returns {Promise<string>} - 'accepted', 'dismissed', or 'unavailable'
 */
export const triggerInstall = async () => {
  if (!deferredInstallEvent) {
    return 'unavailable';
  }
  
  try {
    // Show the install prompt
    deferredInstallEvent.prompt();
    
    // Wait for the user's choice
    const { outcome } = await deferredInstallEvent.userChoice;
    
    // Clear the deferred event (can only be used once)
    deferredInstallEvent = null;
    
    return outcome || 'unavailable';
  } catch (err) {
    console.warn('[InstallHelper] Install prompt error:', err);
    return 'unavailable';
  }
};

// ============================================================================
// Strategy Mapping
// ============================================================================

/**
 * Get the install strategy and content based on environment
 * @param {Object} env - Environment detection result
 * @param {boolean} supportsBeforeInstallPrompt - Whether native prompt is available
 * @returns {Object|null} - Strategy object or null if already installed
 */
export const getInstallStrategy = (env, supportsBeforeInstallPrompt) => {
  // If already installed, don't show anything
  if (env.isStandalone) {
    return null;
  }
  
  const { label, isMac } = env;
  
  // Android + Chromium
  if (label === 'androidChromium') {
    if (supportsBeforeInstallPrompt) {
      return {
        type: 'pwaPrompt',
        title: 'Installa l\'app',
        description: 'Aggiungi Giochini di Parole alla tua schermata home per un accesso più rapido e un\'esperienza migliore.',
        buttonText: 'Installa',
        steps: null
      };
    } else {
      return {
        type: 'menuInstructions',
        title: 'Aggiungi alla schermata home',
        description: 'Segui questi passaggi per installare l\'app sul tuo dispositivo:',
        steps: [
          { icon: 'fa-ellipsis-vertical', text: 'Tocca il menu del browser (⋮) in alto a destra' },
          { icon: 'fa-plus-square', text: 'Seleziona "Installa app" o "Aggiungi a schermata Home"' },
          { icon: 'fa-check', text: 'Conferma toccando "Installa"' }
        ]
      };
    }
  }
  
  // Android + non-Chromium (Firefox, etc.)
  if (label === 'androidOther') {
    return {
      type: 'menuInstructions',
      title: 'Aggiungi alla schermata home',
      description: 'Segui questi passaggi per installare l\'app sul tuo dispositivo:',
      steps: [
        { icon: 'fa-ellipsis-vertical', text: 'Tocca il menu del browser (⋮) in alto a destra' },
        { icon: 'fa-plus-square', text: 'Seleziona "Installa" o "Aggiungi a schermata Home"' },
        { icon: 'fa-check', text: 'Conferma toccando "Aggiungi"' }
      ]
    };
  }
  
  // iOS (all browsers use WebKit, so same instructions)
  if (label === 'iosSafariLike') {
    return {
      type: 'shareSheetInstructions',
      title: 'Aggiungi alla schermata home',
      description: 'Segui questi passaggi per installare l\'app sul tuo dispositivo:',
      steps: [
        { icon: 'fa-arrow-up-from-bracket', text: 'Tocca il pulsante Condividi (quadrato con freccia verso l\'alto) nella barra del browser' },
        { icon: 'fa-plus-square', text: 'Scorri e tocca "Aggiungi alla schermata Home"' },
        { icon: 'fa-check', text: 'Tocca "Aggiungi" in alto a destra' }
      ]
    };
  }
  
  // Desktop Chromium
  if (label === 'desktopChromium') {
    if (supportsBeforeInstallPrompt) {
      return {
        type: 'pwaPrompt',
        title: 'Installa l\'app',
        description: 'Installa Giochini di Parole sul tuo computer per un accesso rapido dalla barra delle applicazioni.',
        buttonText: 'Installa',
        steps: null
      };
    } else {
      return {
        type: 'menuInstructions',
        title: 'Installa l\'app',
        description: 'Segui questi passaggi per installare l\'app:',
        steps: [
          { icon: 'fa-download', text: 'Cerca l\'icona di installazione nella barra degli indirizzi' },
          { icon: 'fa-ellipsis-vertical', text: 'Oppure apri il menu (⋮) in alto a destra' },
          { icon: 'fa-window-maximize', text: 'Seleziona "Installa Giochini di Parole..."' }
        ]
      };
    }
  }
  
  // Desktop Safari
  if (label === 'desktopSafari') {
    return {
      type: 'menuInstructions',
      title: 'Aggiungi al Dock',
      description: 'Segui questi passaggi per aggiungere l\'app al Dock:',
      steps: [
        { icon: 'fa-bars', text: 'Apri il menu "File" nella barra dei menu' },
        { icon: 'fa-plus-square', text: 'Seleziona "Aggiungi al Dock..."' },
        { icon: 'fa-check', text: 'Conferma cliccando "Aggiungi"' }
      ]
    };
  }
  
  // Desktop Firefox / others
  if (label === 'desktopOther') {
    const shortcutKey = isMac ? '⌘+D' : 'Ctrl+D';
    return {
      type: 'bookmarkShortcut',
      title: 'Aggiungi ai preferiti',
      description: 'Firefox non supporta l\'installazione come app, ma puoi salvare questa pagina nei preferiti:',
      steps: [
        { icon: 'fa-keyboard', text: `Premi ${shortcutKey} per aggiungere ai preferiti` },
        { icon: 'fa-folder', text: 'Scegli dove salvare il segnalibro' },
        { icon: 'fa-check', text: 'Clicca "Salva" per confermare' }
      ]
    };
  }
  
  // Fallback
  return {
    type: 'bookmarkShortcut',
    title: 'Salva questa pagina',
    description: 'Aggiungi questa pagina ai preferiti per un accesso rapido:',
    steps: [
      { icon: 'fa-star', text: 'Usa la funzione preferiti del tuo browser' },
      { icon: 'fa-folder', text: 'Scegli dove salvare' },
      { icon: 'fa-check', text: 'Conferma' }
    ]
  };
};

// ============================================================================
// Local Storage Helpers
// ============================================================================

const INSTALL_DISMISSED_KEY = 'install-helper-dismissed';
const LEGACY_INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';
const INSTALL_SHOWN_COUNT_KEY = 'install-helper-shown-count';
const INSTALL_DISMISS_COOLDOWN_DAYS = 30;

const isDismissedWithinCooldown = (rawValue) => {
  const dismissedTimestamp = parseInt(rawValue, 10);
  if (!Number.isFinite(dismissedTimestamp)) {
    return false;
  }

  const dismissedDate = new Date(dismissedTimestamp);
  const cooldownStart = new Date();
  cooldownStart.setDate(cooldownStart.getDate() - INSTALL_DISMISS_COOLDOWN_DAYS);
  return dismissedDate > cooldownStart;
};

/**
 * Check if the install helper has been dismissed
 * @returns {boolean}
 */
export const isInstallHelperDismissed = () => {
  try {
    const dismissed = safeLocalStorageGetItem(INSTALL_DISMISSED_KEY);
    if (dismissed && isDismissedWithinCooldown(dismissed)) {
      return true;
    }

    // Backward compatibility: read old key and migrate when present.
    const legacyDismissed = safeLocalStorageGetItem(LEGACY_INSTALL_DISMISSED_KEY);
    if (legacyDismissed && isDismissedWithinCooldown(legacyDismissed)) {
      safeLocalStorageSetItem(INSTALL_DISMISSED_KEY, legacyDismissed);
      return true;
    }

    return false;
  } catch (_) {
    return false;
  }
};

/**
 * Mark the install helper as dismissed
 */
export const markInstallHelperDismissed = () => {
  const timestamp = Date.now().toString();
  safeLocalStorageSetItem(INSTALL_DISMISSED_KEY, timestamp);
  // Keep writing old key for compatibility with existing tooling/debug checks.
  safeLocalStorageSetItem(LEGACY_INSTALL_DISMISSED_KEY, timestamp);
};

/**
 * Get how many times the install helper has been shown
 * @returns {number}
 */
export const getInstallHelperShownCount = () => {
  try {
    return parseInt(safeLocalStorageGetItem(INSTALL_SHOWN_COUNT_KEY) || '0', 10);
  } catch (_) {
    return 0;
  }
};

/**
 * Increment the install helper shown count
 */
export const incrementInstallHelperShownCount = () => {
  try {
    const count = getInstallHelperShownCount() + 1;
    safeLocalStorageSetItem(INSTALL_SHOWN_COUNT_KEY, count.toString());
    return count;
  } catch (_) {
    return 0;
  }
};

/**
 * Check if we should show the install helper modal
 * Requirements:
 * - 3+ game sessions played (across all time)
 * - Games played on 2+ different days
 * - Not dismissed within last 30 days
 * - Not already installed (standalone mode)
 * 
 * @returns {boolean}
 */
export const shouldShowInstallHelperModal = () => {
  // Don't show if already dismissed (30-day cooldown)
  if (isInstallHelperDismissed()) {
    return false;
  }
  
  // Don't show if already installed
  const env = detectEnvironment();
  if (env.isStandalone) {
    return false;
  }
  
  // Check completed games from streak data
  try {
    const streakData = localStorage.getItem('streak_data');
    if (!streakData) {
      return false; // No streak data = new user, don't show yet
    }
    
    const data = JSON.parse(streakData);
    const completedDates = data.completedDates || {};
    const completedDatesList = Object.keys(completedDates);
    
    // Requirement 1: Games played on 2+ different days
    const uniqueDays = completedDatesList.length;
    if (uniqueDays < 2) {
      return false;
    }
    
    // Requirement 2: 3+ game sessions played
    // We count total game sessions from localStorage (not just unique days)
    let totalGameSessions = 0;
    const gameIds = ['ortografia', 'memoria', 'collegamenti', 'scalinata', 'cloze', 'spettri', 'serpentoni'];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      // Check if key matches game_date pattern
      const matchingGame = gameIds.find(g => key.startsWith(`${g}_`));
      if (!matchingGame) continue;
      
      try {
        const gameData = JSON.parse(localStorage.getItem(key));
        if (!gameData || Object.keys(gameData).length === 0) continue;
        
        // Check if game was actually played (has meaningful progress)
        let hasProgress = false;
        switch (matchingGame) {
          case 'ortografia':
            hasProgress = gameData.score > 0 || (Array.isArray(gameData.submittedWords) && gameData.submittedWords.length > 0);
            break;
          case 'memoria':
            hasProgress = gameData.gameOver === true || (Array.isArray(gameData.matched) && gameData.matched.length > 0);
            break;
          case 'collegamenti':
            hasProgress = gameData.gameOver === true || (Array.isArray(gameData.foundGroups) && gameData.foundGroups.length > 0);
            break;
          case 'scalinata':
            const guesses = gameData.guesses || {};
            hasProgress = Object.keys(guesses).some(level => Array.isArray(guesses[level]) && guesses[level].length > 0) || gameData.gameOver === true;
            break;
          case 'cloze':
            hasProgress = gameData.gameOver === true || gameData.completed === true;
            break;
          case 'spettri':
            hasProgress = gameData.gameOver === true || gameData.completed === true;
            break;
          case 'serpentoni':
            hasProgress =
              (typeof gameData.seq === 'number' && gameData.seq > 0) ||
              gameData.status === 'won' ||
              gameData.status === 'lost' ||
              gameData.spangram_found === true ||
              (Array.isArray(gameData.found_word_indices) && gameData.found_word_indices.length > 0);
            break;
          default:
            hasProgress = Object.keys(gameData).length > 0;
        }
        
        if (hasProgress) {
          totalGameSessions++;
        }
      } catch (_) {
        // Skip invalid entries
      }
    }
    
    // Requirement 2: 3+ game sessions
    if (totalGameSessions < 3) {
      return false;
    }
    
    return true;
  } catch (_) {
    return false;
  }
};
